import { useState, useEffect } from "react";
import { useIntl } from "react-intl";

import { getAllGisLayers, updateGisLayer } from "services/requests/v1/gislayer";

import {
    GisLayerTableType,
    SetRequestError,
    SubscribeContentType,
    UnsubscribeContentType,
} from "types/generic/index";

import { GoAiguaButton } from "components/generic";
import ConfirmationUpdateLayerModal from "components/generic/confirmationupdatelayermodal";
import LayerMasterMap from "components/adhoc/layermastermap";
import GoAiguaModal from "components/generic/goaiguamodal";

interface GisLayerJsonType {
    rowSelected: GisLayerTableType[];
    rowData: GisLayerTableType[];
    rowSelectedWithId: GisLayerTableType[];
    contentName: string;
    setRowSelected: React.Dispatch<React.SetStateAction<GisLayerTableType[]>>;
    setRowData: React.Dispatch<React.SetStateAction<GisLayerTableType[]>>;
    setRowSelectedWithId: (rowSelectedWithId: GisLayerTableType[]) => void;
    subscribeContent: SubscribeContentType;
    unsubscribeContent: UnsubscribeContentType;
    setError: SetRequestError;
}

const GisLayerJson = ({
    rowSelected,
    rowData,
    rowSelectedWithId,
    contentName,
    setRowData,
    setRowSelected,
    setRowSelectedWithId,
    setError,
    subscribeContent,
    unsubscribeContent,
}: GisLayerJsonType) => {
    const intl = useIntl();
    const [rowIsEdited, setRowIsEdited] = useState<string>();
    const [checkStatus, setCheckStatus] = useState<"enabled" | "disabled">(
        "disabled"
    );
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [sintaxisError, setSintaxisError] = useState<boolean>(false);

    const handleSaveClick = (edited: boolean) => {
        if (edited === true) {
            if (rowIsEdited) {
                subscribeContent(contentName);

                try {
                    let objectEdited = JSON.parse(rowIsEdited);
                    if (!objectEdited.id) {
                        let id = rowSelectedWithId[0].id;
                        objectEdited = { ...objectEdited, id: id };
                    }
                    if (objectEdited) {
                        updateGisLayer(objectEdited).then((res) => {
                            const { id, ...rowSelectedNoId } = objectEdited;

                            setRowSelectedWithId([objectEdited]);
                            setRowSelected([rowSelectedNoId]);
                            getAllGisLayers().then((res) => {
                                setRowData(res.data);
                            });
                        });
                    }

                    unsubscribeContent(contentName, true);
                } catch {
                    setSintaxisError(true);
                    setError(contentName);
                }
            }

            setIsOpen(false);
        } else {
            setIsOpen(false);
        }
    };

    const closeModal = () => setIsOpen(false);

    useEffect(() => {
        if (rowSelected.length !== 0) {
            setRowIsEdited(JSON.stringify(rowSelected[0], undefined, 4));
        } else {
            setRowIsEdited("");
        }
    }, [rowSelected]);

    useEffect(() => {
        if (
            rowSelected.length > 0 &&
            JSON.stringify(rowSelected[0], undefined, 4) !== rowIsEdited
        ) {
            setCheckStatus("enabled");
        } else {
            setCheckStatus("disabled");
        }
    }, [rowIsEdited, rowSelected]);

    return (
        <>
            <div className="c-layer-json__header">
                <div className="c-layer-json__header__title">
                    {intl.formatMessage({ id: "app.gis.json_editor" })}
                </div>
            </div>
            <div className="c-layer-json__editor">
                <div className="c-layer-json__editor__editor-json">
                    <div className="c-layer-json__editor__editor-json__title">
                        {intl.formatMessage({
                            id: "app.gis.select_layer_to_preview",
                        })}
                    </div>
                    <textarea
                        className="c-layer-json__editor__editor-json__text"
                        value={rowIsEdited}
                        onChange={(ev) => {
                            setRowIsEdited(ev.target.value);
                        }}
                    />
                    {sintaxisError && (
                        <div className="c-layer-json__editor__error">
                            {intl.formatMessage({ id: "app.gis.sintaxError" })}
                        </div>
                    )}
                    <div className="c-layer-json__editor__editor-json__buttons">
                        <GoAiguaButton
                            text={intl.formatMessage({
                                id: "app.gis.revert",
                            })}
                            status={checkStatus}
                            onClick={() => {
                                setRowIsEdited(
                                    JSON.stringify(rowSelected[0], undefined, 4)
                                );
                            }}
                        />
                        <GoAiguaButton
                            text={intl.formatMessage({
                                id: "app.gis.save_changes",
                            })}
                            status={checkStatus}
                            onClick={() => {
                                setIsOpen(true);
                            }}
                        />
                    </div>
                </div>
                <div className="c-layer-json__editor__map">
                    <div className="c-layer-json__editor__map__title">
                        {intl.formatMessage({
                            id: "app.gis.preview_on_map",
                        })}
                    </div>
                    <div className="c-layer-json__editor__map__map">
                        <LayerMasterMap
                            rowSelected={rowSelected}
                            rowSelectedWithId={rowSelectedWithId}
                            layers={rowData}
                        />
                    </div>
                </div>
            </div>
            <GoAiguaModal
                headerTitle={intl.formatMessage({ id: "app.hierarchy.modify" })}
                isOpen={isOpen}
                setIsOpen={closeModal}
            >
                <ConfirmationUpdateLayerModal
                    details={rowIsEdited}
                    closeModal={closeModal}
                    callback={handleSaveClick}
                />
            </GoAiguaModal>
        </>
    );
};

export default GisLayerJson;
